import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: '首页', path: '/' },
  { label: '电影', path: '/category/movie' },
  { label: '电视剧', path: '/category/tv' },
  { label: '综艺', path: '/category/variety' },
  { label: '动漫', path: '/category/anime' },
  { label: '关于我们', path: '/about' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex items-center justify-between px-4 md:px-6 h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
          <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
            <i className="ri-movie-2-line text-background-50 text-xl"></i>
          </div>
          <span className={`font-heading text-xl font-bold transition-colors duration-300 ${
            scrolled ? 'text-foreground-900' : 'text-white'
          }`}>
            麻花影视
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  isActive
                    ? scrolled
                      ? 'bg-primary-500 text-white'
                      : 'bg-white/20 text-white'
                    : scrolled
                      ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-200'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </div>

        {/* Search + mobile toggle */}
        <div className="flex items-center gap-3">
          <div className={`hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer ${
            scrolled
              ? 'bg-background-200 text-foreground-600 hover:bg-background-300'
              : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
          }`}>
            <i className="ri-search-line text-sm"></i>
            <input
              type="text"
              placeholder="搜索电影、电视剧..."
              className={`bg-transparent outline-none text-sm w-36 placeholder-current/50 ${
                scrolled ? 'text-foreground-900' : 'text-white'
              }`}
            />
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              scrolled
                ? 'text-foreground-700 hover:bg-background-200'
                : 'text-white hover:bg-white/10'
            }`}
            aria-label="菜单"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 shadow-lg animate-[fadeIn_0.2s_ease]">
          <div className="px-4 py-3 space-y-1">
            {/* Mobile search */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background-100 text-foreground-600 mb-2 sm:hidden">
              <i className="ri-search-line text-sm"></i>
              <input
                type="text"
                placeholder="搜索电影、电视剧..."
                className="bg-transparent outline-none text-sm w-full text-foreground-900 placeholder-foreground-400"
              />
            </div>
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    isActive
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-700 hover:bg-background-200 hover:text-foreground-950'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
}